import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Modal from '../../common/Modal';
import HeaderSheet from '../../common/HeaderSheet';
import ModalAction from '../../common/ModalAction';
import InstrumentRow from '../../common/InstrumentRow';
import ModalButton from '../../common/ModalButton';
import SampleLoadStatus from '../../common/SampleLoadStatus';
import RangeSlider from '../../common/RangeSlider';
import MidiPiano from '../../MidiPiano';
import ChordBarsTransport, { ChordBarsMixer } from '../../ChordBarsTransport';
import MidiSummary, { MidiKeyRow } from '../../MidiSummary';
import Icon from '../../common/Icon';

import { ReactComponent as FolderIcon } from '../../../icons/folder.svg';
import { ReactComponent as XmarkIcon } from '../../../icons/xmark.svg';
import { ReactComponent as MelodyIcon } from '../../../icons/music-single-note.svg';
import { ReactComponent as PlayIcon } from '../../../icons/play-solid.svg';
import { ReactComponent as SlidersIcon } from '../../../icons/sliders.svg';
import { ReactComponent as SettingsIcon } from '../../../icons/settings.svg';
import { ReactComponent as StopIcon } from '../../../icons/square-rounded.svg';
import useMidiPreview from '../../../hooks/useMidiPreview';
import useSpacePlay from '../../../hooks/useSpacePlay';
import { getPartInstrumentId } from '../../../utils/midiInstruments';
import { getBandInstrumentLabel } from '../../../utils/parts';
import { isSameKey, spellKey } from '../../../utils/partKey';
import {
  parseMidiFile,
  countMidiNotes,
  getMidiJsonBytes,
  getMidiTranspositionToKey,
  formatMidiDuration,
  isSingleLine,
  LEAD_IN_TRIMS,
  OCTAVE_DOUBLINGS,
  MAX_MIDI_JSON_BYTES,
  MAX_MIDI_DURATION_SECONDS,
} from '../../../utils/midiJson';

import './PartMidiUploadModal.scss';

// Binary MIDI expands roughly 7x once parsed, so the file size is only a cheap pre-filter that
// avoids parsing something huge — the limit that matters is on the JSON that actually gets stored.
const MAX_FILE_BYTES = 64 * 1024;

// Shown on the opening prompt so the limit is known before a file is chosen, rather than only
// turning up as a rejection afterwards. Dropped once a clip is attached: by then the limit has
// been demonstrated rather than described, and the picker is better off short. Derived from the
// constant the parser actually enforces so the two cannot drift apart.
const MAX_DURATION = formatMidiDuration(MAX_MIDI_DURATION_SECONDS);

// A pickup's whole bars, then its nudge in quarter beats up to the bar line — anything further is a bar more
const MAX_PICKUP_BARS = 4;
const PICKUP_BEAT_STEP = 0.25;

/**
 * One setting as a row of segments, with only the chosen option's hint under it — a line rather
 * than a card per option, so the settings sheet stays short.
 */
const Segmented = ({ name, label, options, value, onChange, children }) => {
  const selected = options.find((option) => option.value === value);

  return (
    <fieldset className="setting">
      <legend>{label}</legend>
      <div className="segments">
        {options.map((option) => (
          <label key={option.value || 'none'} className={option.value === value ? 'segment selected' : 'segment'}>
            <input type="radio" name={name} checked={option.value === value} onChange={() => onChange(option.value)} />
            {option.label}
          </label>
        ))}
      </div>
      {selected?.hint && <p className="hint">{selected.hint}</p>}
      {children}
    </fieldset>
  );
};

Segmented.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string, hint: PropTypes.string }),
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node,
};

const PartMidiUploadModal = ({
  isOpen,
  onClose,
  part,
  onConfirm,
  partKey,
  soundingKey,
  songKey,
  loadedSongKey,
  beatsPerBar = 4,
  isAdmin = false,
}) => {
  const { t } = useTranslation();
  const [midiJson, setMidiJson] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState('');

  // One header sheet at a time: the levels, or how the clip plays
  const [openSheet, setOpenSheet] = useState(null);
  const toggleSheet = (sheet) => setOpenSheet((open) => (open === sheet ? null : sheet));
  const closeSheet = () => setOpenSheet(null);

  // An instrument picked from the browser that is waiting to be played once the selection lands
  const [pendingPlayId, setPendingPlayId] = useState(null);

  const instrumentId = getPartInstrumentId(midiJson);

  // The filename is about the file the admin just picked off their own machine, so only they see
  // it — everyone else editing the song gets a plain label for the same row
  const fileLabel = (isAdmin && midiJson?.name) || t('dialogs.midiUpload.fileLabel');

  // The key the preview plays in, and the one shown above it. This is the *sounding* key — the
  // part's key with the song's transpose already applied by the form — not the key field, so what
  // an admin hears here is what a viewer will hear. partKey stays the anchor stamped onto a new
  // upload, and the two must not be swapped: anchoring a clip to the transposed key would have
  // View transpose it a second time.
  const previewKey = soundingKey || partKey;

  // A part that modulates is anchored to its own key rather than the song's, which is the whole
  // point of the part key — but it means the key above the preview disagrees with the key field in
  // the form behind this modal. Said out loud here, because unexplained it reads as a bug.
  const isOwnKey = Boolean(partKey && songKey) && !isSameKey(partKey, songKey);

  // Which key the recording itself is in. Nothing in a MIDI file says, so an admin who played
  // along with a transposed song has to tell us — before this, every upload was assumed to be in
  // the song's original key and one recorded in the transposed key got shifted a second time.
  //
  // The answer is stored as the clip's sourceKey, which is what every shift is already measured
  // from: naming the original key has the clip converted just as the song's chords are, and naming
  // the transposed key leaves it alone, because it is already where the song is going.
  const isTransposed = Boolean(soundingKey && partKey && !isSameKey(soundingKey, partKey));
  const recordedKey = midiJson?.sourceKey || partKey;

  // Applied to the attached clip rather than only to a fresh upload, so a clip that came in under
  // the wrong assumption can be corrected in place — and the preview re-pitches as it is switched,
  // which is the fastest way to tell which answer is the right one
  const handleRecordedKeyChange = (key) =>
    setMidiJson((previous) => (previous ? { ...previous, sourceKey: key } : previous));

  // Which instrument the clip was played for, where the part carries a chart for each of them.
  // Without it the same melody sounds under every chart, including the ones whose chords it was
  // never played over. Empty is "all of them", which is what every clip uploaded before this was.
  const assignedInstruments = part?.assignedInstruments || [];
  const playsWith = midiJson?.playsWith || '';

  // Only worth asking where there is a choice to make: one instrument, or none, and the clip
  // plays with whatever the part has
  const showsPlaysWith = Boolean(midiJson) && assignedInstruments.length >= 2;

  // Dropped from the clip rather than stored empty, so "all" is the absence the reader already
  // treats as all — and a clip that never had the field stays byte for byte what it was
  const handlePlaysWithChange = (instrument) =>
    setMidiJson((previous) => {
      if (!previous) return previous;

      const { playsWith: current, ...rest } = previous;

      return instrument ? { ...rest, playsWith: instrument } : rest;
    });

  // How much of the silence the clip opens with is dropped before it plays: its empty bars, keeping
  // a pickup where it was played, or everything up to the first note. Bars is the absence, as with
  // playsWith, so a clip saved before the choice existed reads as it always played.
  const trimLeadIn = midiJson?.trimLeadIn || LEAD_IN_TRIMS.bars;
  const trimLeadInOptions = [
    {
      value: LEAD_IN_TRIMS.bars,
      label: t('dialogs.midiUpload.emptyBars'),
      hint: t('dialogs.midiUpload.emptyBarsHint'),
    },
    {
      value: LEAD_IN_TRIMS.silence,
      label: t('dialogs.midiUpload.allSilence'),
      hint: t('dialogs.midiUpload.allSilenceHint'),
    },
  ];

  const handleTrimLeadInChange = (value) =>
    setMidiJson((previous) => {
      if (!previous) return previous;

      const { trimLeadIn: current, ...rest } = previous;

      return value === LEAD_IN_TRIMS.silence ? { ...rest, trimLeadIn: value } : rest;
    });

  // Which octave every note is mirrored into as it plays, for a line played with one hand. Off is the
  // absence, like trimLeadIn. Chords can be doubled too, but they're warned about since they thicken.
  const octaveDouble = midiJson?.octaveDouble || '';
  const octaveDoubleOptions = [
    { value: '', label: t('dialogs.midiUpload.off'), hint: t('dialogs.midiUpload.offHint') },
    {
      value: OCTAVE_DOUBLINGS.below,
      label: t('dialogs.midiUpload.octaveBelow'),
      hint: t('dialogs.midiUpload.octaveBelowHint'),
    },
    {
      value: OCTAVE_DOUBLINGS.above,
      label: t('dialogs.midiUpload.octaveAbove'),
      hint: t('dialogs.midiUpload.octaveAboveHint'),
    },
  ];
  const playsChords = Boolean(midiJson) && !isSingleLine(midiJson);

  const handleOctaveDoubleChange = (value) =>
    setMidiJson((previous) => {
      if (!previous) return previous;

      const { octaveDouble: current, ...rest } = previous;

      return value ? { ...rest, octaveDouble: value } : rest;
    });

  // How far ahead of its part the clip comes in, for a pickup played over the part before it. Each
  // is dropped at 0, like playsWith, so a clip without a pickup stays what it was.
  const pickupBars = midiJson?.pickupBars || 0;
  const pickupBeats = midiJson?.pickupBeats || 0;

  const handlePickupChange = (field, value) =>
    setMidiJson((previous) => {
      if (!previous) return previous;

      const { [field]: current, ...rest } = previous;

      return value ? { ...rest, [field]: value } : rest;
    });

  // The two answers that make sense for a recording made against the song as it stands. A clip
  // attached earlier can be in neither — the song's transpose may have moved since it was
  // uploaded — so its own key is offered beside them rather than leaving the row with nothing
  // selected and no way to tell what the clip is anchored to.
  const recordedKeyOptions = [
    {
      key: partKey,
      label: t('dialogs.midiUpload.original'),
      hint: t('dialogs.midiUpload.transposedTo', { key: spellKey(soundingKey) }),
    },
    { key: soundingKey, label: t('dialogs.midiUpload.transposed'), hint: t('dialogs.midiUpload.asRecorded') },
  ];

  if (recordedKey && !recordedKeyOptions.some((option) => option.key === recordedKey)) {
    recordedKeyOptions.push({
      key: recordedKey,
      label: t('dialogs.midiUpload.asUploaded'),
      hint: t('dialogs.midiUpload.transposedTo', { key: spellKey(soundingKey) }),
    });
  }

  // A clip uploaded against an earlier key follows the key field live, so the preview always
  // sounds the way the part will. The stored clip keeps its own sourceKey and is never rewritten.
  //
  // The fallback matters as much as the clip's own key here: a clip that predates sourceKey has
  // to be anchored to the key the song was *loaded* with, not to the field, which is the very
  // thing being changed — anchoring it to the field would compare a key against itself and
  // always come out at zero.
  const semitones = getMidiTranspositionToKey(midiJson, previewKey, loadedSongKey);

  // Its sample loader watches whatever download is running for the selected instrument; it starts
  // nothing by itself, so opening the modal costs no bandwidth
  const player = useMidiPreview(midiJson, instrumentId, isOpen, semitones);
  const { isLoading, isPlaying, play, stop, duration, activeNotes, playbackMidi, samples } = player;
  useSpacePlay(player, isOpen);

  // Reopening starts from whatever is currently attached to the part, so cancelling
  // out of a file pick leaves the saved MIDI alone
  useEffect(() => {
    if (!isOpen) return;

    const attached = part?.midi ?? null;

    // A clip saved before sourceKey existed gets anchored the first time it is opened, to the
    // key the song was loaded with — which is what it was uploaded as. Pinning it onto the clip
    // here rather than working it out at playback means the anchor is decided once, from a value
    // that cannot move, instead of being re-derived against a key field the user is editing.
    setMidiJson(
      attached && !attached.sourceKey && loadedSongKey ? { ...attached, sourceKey: loadedSongKey } : attached,
    );
    setError('');
    setOpenSheet(null);
  }, [isOpen, part, loadedSongKey]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];

    // Clearing the input lets the same file be picked again after a failed read
    event.target.value = '';

    if (!file) return;

    setError('');

    if (file.size > MAX_FILE_BYTES) {
      setError(t('dialogs.midiUpload.tooLarge'));
      return;
    }

    setIsParsing(true);

    try {
      const parsed = await parseMidiFile(await file.arrayBuffer(), file.name);

      if (countMidiNotes(parsed) === 0) {
        setMidiJson(null);
        setError(t('dialogs.midiUpload.noNotes'));
        return;
      }

      if (parsed.duration > MAX_MIDI_DURATION_SECONDS) {
        setMidiJson(null);
        setError(t('dialogs.midiUpload.tooLong', { length: formatMidiDuration(parsed.duration), max: MAX_DURATION }));
        return;
      }

      if (getMidiJsonBytes(parsed) > MAX_MIDI_JSON_BYTES) {
        setMidiJson(null);
        setError(t('dialogs.midiUpload.tooManyNotes'));
        return;
      }

      // Anchors the clip to the key it was written in, so a later key edit re-derives the
      // offset rather than quietly relabelling the same audio
      setMidiJson({ ...parsed, sourceKey: partKey });
    } catch (parseError) {
      setMidiJson(null);
      setError(t('dialogs.midiUpload.unreadable'));
    } finally {
      setIsParsing(false);
    }
  };

  // The choice rides along inside the MIDI JSON, so it saves with the clip and is dropped
  // along with it when the MIDI is removed
  const handleInstrumentChange = (newInstrumentId) => {
    setMidiJson((previous) => (previous ? { ...previous, instrumentId: newInstrumentId } : previous));

    // Pulled straight away so the admin hears what they are committing viewers to before saving.
    // The id is passed in rather than read back off state, which has not updated yet.
    samples.download(newInstrumentId);
  };

  // Auditioning a row means selecting it and playing it, which cannot be done in one go: `play`
  // closes over the instrument it was built with, so calling it here would sound whatever was
  // selected a moment ago. Recording the intent instead lets the effect below fire it once the
  // selection has actually landed and `play` has been rebuilt around it.
  const handlePreview = (previewId) => {
    // The control on a sounding row is a stop, so tapping it has to end there. Stopping and then
    // falling through to the branch below — same instrument, so play it — is a restart, which
    // leaves the row impossible to stop at all.
    const isSounding = isPlaying && previewId === instrumentId;

    if (isPlaying) stop();
    if (isSounding) return;

    if (previewId === instrumentId) {
      play();

      return;
    }

    handleInstrumentChange(previewId);
    setPendingPlayId(previewId);
  };

  useEffect(() => {
    if (!pendingPlayId || pendingPlayId !== instrumentId) return;

    setPendingPlayId(null);
    play();
  }, [pendingPlayId, instrumentId, play]);

  const handleRemove = () => {
    stop();
    setMidiJson(null);
    setError('');
  };

  const handleSave = () => {
    onConfirm(part?.id ?? null, midiJson);
    onClose();
  };

  // Play, in the header like the other preview dialogs. Nothing to play until a file is chosen, so
  // it is absent until then — and so are the levels.
  const transport = midiJson ? (
    <>
      <ModalAction
        icon={isPlaying ? <StopIcon className="solid small" /> : <PlayIcon className="solid" />}
        label={player.playLabel}
        active={isPlaying}
        busy={player.playBusy}
        progress={player.playProgress}
        disabled={!player.canPlay}
        onClick={isPlaying ? stop : play}
      />
      <ModalAction
        icon={<SlidersIcon />}
        label={t('player.volume')}
        active={openSheet === 'mixer'}
        onClick={() => toggleSheet('mixer')}
      />
      <ModalAction
        icon={<SettingsIcon />}
        label={t('dialogs.midiUpload.playbackSettings')}
        active={openSheet === 'settings'}
        onClick={() => toggleSheet('settings')}
      />
    </>
  ) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('dialogs.midiUpload.title')}
      className="PartMidiUploadModal"
      evenFooter
      actions={transport}
      footer={
        <>
          <ModalButton variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </ModalButton>
          <ModalButton onClick={handleSave} disabled={isParsing}>
            {t('common.save')}
          </ModalButton>
        </>
      }
      sheet={
        midiJson && (
          <>
            <ChordBarsMixer
              isOpen={openSheet === 'mixer'}
              onClose={closeSheet}
              player={player}
              idPrefix="part-midi-upload"
              instrumentIcon={<MelodyIcon className="stroke-icon" aria-hidden="true" />}
            />
            <HeaderSheet
              isOpen={openSheet === 'settings'}
              onClose={closeSheet}
              label={t('dialogs.midiUpload.playbackSettings')}
              className="midi-settings"
            >
              <Segmented
                name="trimLeadIn"
                label={t('dialogs.midiUpload.trimLeadIn')}
                options={trimLeadInOptions}
                value={trimLeadIn}
                onChange={handleTrimLeadInChange}
              />
              <Segmented
                name="octaveDouble"
                label={t('dialogs.midiUpload.octaveDouble')}
                options={octaveDoubleOptions}
                value={octaveDouble}
                onChange={handleOctaveDoubleChange}
              >
                {playsChords && octaveDouble && <p className="warning">{t('dialogs.midiUpload.chordsWarning')}</p>}
              </Segmented>
              <fieldset className="setting">
                <legend>{t('dialogs.midiUpload.pickup')}</legend>
                <div className="pickup">
                  <RangeSlider
                    id="part-midi-pickup-bars"
                    label={t('dialogs.midiUpload.bars')}
                    ariaLabel={t('dialogs.midiUpload.pickupBars')}
                    value={pickupBars}
                    onChange={(event) => handlePickupChange('pickupBars', Number(event.target.value))}
                    max={MAX_PICKUP_BARS}
                    step={1}
                    formatValue={String}
                  />
                  <RangeSlider
                    id="part-midi-pickup-beats"
                    label={t('dialogs.midiUpload.beats')}
                    ariaLabel={t('dialogs.midiUpload.pickupBeats')}
                    value={Math.min(pickupBeats, beatsPerBar - PICKUP_BEAT_STEP)}
                    onChange={(event) => handlePickupChange('pickupBeats', Number(event.target.value))}
                    max={beatsPerBar - PICKUP_BEAT_STEP}
                    step={PICKUP_BEAT_STEP}
                    formatValue={String}
                  />
                </div>
              </fieldset>
            </HeaderSheet>
          </>
        )
      }
    >
      <MidiKeyRow songKey={previewKey} />

      {isOwnKey && <p className="part-key-note">{t('dialogs.midiUpload.ownKey', { key: spellKey(songKey) })}</p>}

      {isTransposed && midiJson && (
        <fieldset className="recorded-key">
          <legend>{t('dialogs.midiUpload.recordedIn')}</legend>
          {recordedKeyOptions.map(({ key, label, hint }) => (
            <label key={key} className={recordedKey === key ? 'option selected' : 'option'}>
              <input
                type="radio"
                name="recordedKey"
                checked={recordedKey === key}
                onChange={() => handleRecordedKeyChange(key)}
              />
              <span className="name">
                {label} <strong>{spellKey(key)}</strong>
              </span>
              <span className="hint">{hint}</span>
            </label>
          ))}
        </fieldset>
      )}

      {showsPlaysWith && (
        <fieldset className="plays-with">
          <legend>{t('dialogs.midiUpload.playsWith')}</legend>
          {['', ...assignedInstruments].map((instrument) => (
            <label key={instrument || 'all'} className={playsWith === instrument ? 'option selected' : 'option'}>
              <input
                type="radio"
                name="playsWith"
                checked={playsWith === instrument}
                onChange={() => handlePlaysWithChange(instrument)}
              />
              <span className="name">
                <strong>{instrument ? getBandInstrumentLabel(instrument) : t('common.all')}</strong>
              </span>
            </label>
          ))}
        </fieldset>
      )}

      {/* The dashed prompt is for an empty part — it is a target to drop a file on, and it
          says what the limit is before one is chosen. Once a clip is attached there is
          something concrete to name, so it gives way to a row that names it and carries the
          two things left to do with it: swap the file, or take it off the part. */}
      {midiJson ? (
        <div className="attached-file">
          <span className="name" title={fileLabel}>
            {isParsing ? t('dialogs.midiUpload.reading') : fileLabel}
          </span>
          {/* The label is the control on screen, but the input inside it is what a screen
              reader announces, so the name goes there rather than on the label */}
          <label className="row-btn" title={t('dialogs.midiUpload.chooseDifferent')}>
            <Icon icon={<FolderIcon />} />
            <input
              type="file"
              accept=".mid,.midi"
              aria-label={t('dialogs.midiUpload.chooseDifferent')}
              onChange={handleFileChange}
              disabled={isParsing}
            />
          </label>
          <button
            type="button"
            className="row-btn remove"
            onClick={handleRemove}
            title={t('dialogs.midiUpload.remove')}
            aria-label={t('dialogs.midiUpload.remove')}
          >
            <Icon icon={<XmarkIcon />} />
          </button>
        </div>
      ) : (
        <label className="file-picker">
          <span>
            {isParsing ? t('dialogs.midiUpload.reading') : t('dialogs.midiUpload.chooseFile', { max: MAX_DURATION })}
          </span>
          <input type="file" accept=".mid,.midi" onChange={handleFileChange} disabled={isParsing} />
        </label>
      )}

      {error && <p className="error">{error}</p>}

      {midiJson && (
        <div className="midi-summary">
          <MidiSummary midiJson={midiJson} duration={duration} />
          <MidiPiano midiJson={playbackMidi} activeNotes={activeNotes} />
          <ChordBarsTransport player={player}>
            <InstrumentRow
              instrumentId={instrumentId}
              onSelect={handleInstrumentChange}
              player={player}
              browser={{
                downloadedIds: samples.downloadedIds,
                onPreview: handlePreview,
                playingId: isPlaying ? instrumentId : undefined,
                loadingId: isLoading || samples.status === 'loading' ? instrumentId : undefined,
                progress: { loaded: samples.loaded, total: samples.total },
              }}
            />
            <SampleLoadStatus {...samples} />
          </ChordBarsTransport>
        </div>
      )}
    </Modal>
  );
};

PartMidiUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  part: PropTypes.object,
  onConfirm: PropTypes.func.isRequired,
  /** The key this part is in — its own where it modulates, the song's otherwise. The clip is
      anchored to it on upload */
  partKey: PropTypes.string,
  /** The key the part will sound in once the song's transpose is applied — what the preview plays */
  soundingKey: PropTypes.string,
  /** The song's own key in the form, only to say when the part is not in it */
  songKey: PropTypes.string,
  /** The key the song was loaded with, anchoring clips that carry no sourceKey of their own */
  loadedSongKey: PropTypes.string,
  /** The song's, which a pickup's beats run up to before they are a bar */
  beatsPerBar: PropTypes.number,
  /** Only an admin sees the filename of the clip being attached */
  isAdmin: PropTypes.bool,
};

export default PartMidiUploadModal;
